const DYNAMIC_IMPORT_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /failed to load module script/i,
  /loading chunk \S+ failed/i,
];

const DEFAULT_RELOAD_KEY = 'caloriescience:dynamic-import-reload-at';
const DEFAULT_RELOAD_COOLDOWN_MS = 30_000;
const DEFAULT_RELOAD_DELAY_MS = 1_500;

function getErrorText(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name} ${error.message}`.trim();
  }
  if (typeof error === 'string') return error;

  try {
    return JSON.stringify(error);
  } catch {
    return '';
  }
}

export function isDynamicImportLoadError(error: unknown): boolean {
  const text = getErrorText(error);
  return DYNAMIC_IMPORT_ERROR_PATTERNS.some((pattern) => pattern.test(text));
}

export function scheduleReloadForDynamicImportError(
  error: unknown,
  options?: {
    storageKey?: string;
    delayMs?: number;
    cooldownMs?: number;
  },
): boolean {
  if (!isDynamicImportLoadError(error)) return false;
  if (typeof window === 'undefined') return false;

  const storageKey = options?.storageKey ?? DEFAULT_RELOAD_KEY;
  const cooldownMs = options?.cooldownMs ?? DEFAULT_RELOAD_COOLDOWN_MS;
  const delayMs = options?.delayMs ?? DEFAULT_RELOAD_DELAY_MS;
  const now = Date.now();

  try {
    const lastReloadAt = Number(window.sessionStorage.getItem(storageKey) ?? 0);
    if (Number.isFinite(lastReloadAt) && now - lastReloadAt < cooldownMs) {
      return false;
    }
    window.sessionStorage.setItem(storageKey, String(now));
  } catch {
    // Session storage may be unavailable in private/restricted contexts; reload once anyway.
  }

  window.setTimeout(() => {
    window.location.reload();
  }, delayMs);
  return true;
}

export function registerDynamicImportRecovery(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('vite:preloadError', (event) => {
    const preloadError = (event as Event & { payload?: unknown }).payload;
    if (!isDynamicImportLoadError(preloadError)) return;

    event.preventDefault();
    scheduleReloadForDynamicImportError(preloadError, {
      storageKey: 'caloriescience:vite-preload-reload-at',
      delayMs: 0,
    });
  });
}
